
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Authentication overview',
  layout: 'guide',
  description: 'Your guide to building a secure, standards-based OAuth integration with Plaid Core Exchange',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Authentication' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "what-youll-learn"
      }}>{`What you'll learn`}</h2>
      <ul>
        <li parentName="ul">{`Why OAuth is essential for Core Exchange`}</li>
        <li parentName="ul">{`Key concepts and terminology`}</li>
        <li parentName="ul">{`FDX version compatibility`}</li>
        <li parentName="ul">{`Where to get help and test your integration`}</li>
      </ul>
      <h2 {...{
        "id": "overview"
      }}>{`Overview`}</h2>
      <p>{`Plaid Core Exchange supports any spec-compliant OAuth 2.0 implementation, and strongly recommends `}<a parentName="p" {...{
          "href": "https://openid.net/connect/"
        }}>{`OpenID Connect (OIDC)`}</a>{`, an identity layer built on OAuth 2.0. OIDC enables critical features like the returning user experience and centralized consent management through the ID token's `}<inlineCode parentName="p">{`sub`}</inlineCode>{` field (your unique/stable user identifier). Consumers authenticate directly with the Data Provider (you), which issues a secure token to Plaid without sharing credentials or compromising security.`}</p>
      <p>{`Get OAuth right, and you unlock:`}</p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Safe authentication:`}</strong>{` Consumers log in on your domain, not Plaid's.`}</li>
        <li parentName="ul"><strong parentName="li">{`User control:`}</strong>{` Plaid accesses data only when users explicitly authorize it.`}</li>
        <li parentName="ul"><strong parentName="li">{`Industry standards:`}</strong>{` Your integration supports all FDX versions (4.6+). Build to the latest spec for best results.`}</li>
        <li parentName="ul"><strong parentName="li">{`Better UX:`}</strong>{` Returning users get a seamless `}<a parentName="li" {...{
            "href": "/docs/link/returning-user/"
          }}>{`experience`}</a>{` (phone-verified reconnection across multiple apps).`}</li>
      </ul>
      <p>{`In the following sections, learn why OAuth matters, how to build an OAuth 2.0 server (with OIDC strongly recommended), how Plaid's token model works, and how to `}<a parentName="p" {...{
          "href": "/core-exchange/docs/troubleshooting"
        }}>{`troubleshoot common issues`}</a>{`.`}</p>
      <h2 {...{
        "id": "try-the-interactive-demo"
      }}>{`Try the interactive demo`}</h2>
      <p>{`See Core Exchange OAuth in action with `}<a parentName="p" {...{
          "href": "https://app.plaidypus.dev/"
        }}>{`FDX Explorer`}</a>{`, a working demo that illustrates the OAuth flow using Plaid's APIs.`}</p>
      <Image src='/assets/img/core-exchange/plaidypus-api-explorer.png' alt='FDX Explorer' caption='FDX Explorer' expandable={true} mdxType="Image" />
      <p><strong parentName="p">{`What FDX Explorer demonstrates:`}</strong></p>
      <ul>
        <li parentName="ul">{`Complete the OAuth authorization flow with a simulated Data Provider`}</li>
        <li parentName="ul">{`Token exchange and refresh token mechanics`}</li>
        <li parentName="ul">{`Real API calls to Core Exchange endpoints`}</li>
        <li parentName="ul">{`User consent and account selection flows`}</li>
      </ul>
      <p>{`The `}<a parentName="p" {...{
          "href": "https://github.com/plaid/core-exchange-node-example"
        }}>{`source code is on GitHub`}</a>{` (Node.js). Explore the implementation to see how:`}</p>
      <ul>
        <li parentName="ul">{`OIDC discovery and JWKS validation work`}</li>
        <li parentName="ul">{`Authorization codes are exchanged for tokens`}</li>
        <li parentName="ul">{`ID tokens are validated and parsed`}</li>
        <li parentName="ul">{`Refresh tokens maintain long-term access`}</li>
        <li parentName="ul">{`Error handling catches common OAuth issues`}</li>
      </ul>
      <p>{`Run the demo locally, change the code, and experiment. Interact directly to see how OAuth works in practice.`}</p>
      <h2 {...{
        "id": "key-terms"
      }}>{`Key terms`}</h2>
      <p>{`You'll see these terms throughout this guide.`}</p>
      <h3 {...{
        "id": "parties-the-who"
      }}>{`Parties (the who)`}</h3>
      <p><strong parentName="p">{`Consumer:`}</strong>{` The end user who owns the financial accounts.`}</p>
      <p><strong parentName="p">{`Authorized Third Party (ATP):`}</strong>{` The app requesting access to user data (Venmo, Chime, Copilot, etc.).`}</p>
      <p><strong parentName="p">{`Plaid (Data Access Platform / DAP):`}</strong>{` The OAuth client and data platform. Plaid acts as the OAuth client that requests access on behalf of apps.`}</p>
      <p><strong parentName="p">{`Data Provider (DP):`}</strong>{` You, the financial institution that authenticates consumers and issues OAuth tokens to Plaid.`}</p>
      <p><strong parentName="p">{`Core Exchange:`}</strong>{` Plaid's data access platform that connects apps to financial institutions using OAuth 2.0 and OIDC for authentication and FDX specification for data APIs.`}</p>
      <h3 {...{
        "id": "protocols-and-standards-the-framework"
      }}>{`Protocols and standards (the framework)`}</h3>
      <p><strong parentName="p">{`OIDC (OpenID Connect):`}</strong>{` An identity layer built on OAuth 2.0. It lets Plaid verify user identity and obtain a stable user identifier through the ID token's sub field, enabling advanced features like returning user experience.`}</p>
      <p><strong parentName="p">{`FDX (Financial Data Exchange):`}</strong>{` Industry standards for financial data sharing APIs. Core Exchange supports all FDX versions (4.6+). The latest version is recommended for new integrations.`}</p>
      <h3 {...{
        "id": "token-models-and-types-the-what"
      }}>{`Token models and types (the what)`}</h3>
      <p><strong parentName="p">{`User-scoped access token:`}</strong>{` A token issued to Plaid that enables Plaid to efficiently provision access to multiple third-party apps with a single token. This benefits users by providing a seamless experience for returning users and by reducing the need for repeated authorization steps. It also reduces API requests for consumers connecting multiple apps, streamlining management for Data Providers. You may also see this called an `}<em parentName="p">{`aggregator token`}</em>{`, `}<em parentName="p">{`single token`}</em>{`, or `}<em parentName="p">{`universal token`}</em>{` in other Plaid materials; they all describe the same model.`}</p>
      <p><strong parentName="p">{`Access token:`}</strong>{` Short-lived (15 min) token for API access.`}</p>
      <p><strong parentName="p">{`Refresh token:`}</strong>{` Long-lived token used to obtain new access tokens. Should be rotated with each use (preferred) or set to expire after 13+ months to meet reauthorization requirements.`}</p>
      <p><strong parentName="p">{`ID token:`}</strong>{` JWT containing user identity information (OIDC).`}</p>
      <p><strong parentName="p">{`Authorization code:`}</strong>{` Temporary code (`}{`<`}{`10 min) exchanged for tokens.`}</p>
      <h3 {...{
        "id": "key-concepts-the-how"
      }}>{`Key concepts (the how)`}</h3>
      <p><strong parentName="p">{`Consistency key:`}</strong>{` A stable, unique identifier for each consumer (usually the `}<inlineCode parentName="p">{`sub`}</inlineCode>{` field in the ID token). Enables Plaid to recognize returning consumers and show all connections in one place.`}</p>
      <p><strong parentName="p">{`JWKS (JSON Web Key Set):`}</strong>{` Your public keys published at `}<inlineCode parentName="p">{`jwks_uri`}</inlineCode>{`. Plaid uses these to verify the digital signatures on ID tokens.`}</p>
      <p><strong parentName="p">{`Returning consumer experience:`}</strong>{` Consumers authenticate once with your bank, then quickly connect new apps using phone verification. No repeated OAuth flows. `}<a parentName="p" {...{
          "href": "/docs/link/returning-user/"
        }}>{`Learn more`}</a>{`.`}</p>
      <p><strong parentName="p">{`App2App:`}</strong>{` Mobile authentication flow that redirects consumers from an ATP's app directly to your mobile banking app for authentication, then returns them seamlessly. Provides a native mobile experience without web views.`}</p>
      <h3 {...{
        "id": "management-tools-the-where"
      }}>{`Management tools (the where)`}</h3>
      <p><strong parentName="p"><a parentName="strong" {...{
            "href": "/core-exchange/docs/consent-management"
          }}>{`Consent management`}</a>{`:`}</strong>{` Plaid's system for managing authorization records and consent. Provides APIs and a dashboard for viewing connections, revoking access, and tracking reauthorization. The `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/api"
        }}>{`Consent API`}</a>{` is the current, FDX-aligned option; `}<a parentName="p" {...{
          "href": "/core-exchange/docs/consent-management/permissions-manager"
        }}>{`Permissions Manager`}</a>{` is the legacy, Plaid-proprietary predecessor.`}</p>
      <p><strong parentName="p">{`App Directory:`}</strong>{` Plaid's catalog of application metadata (app names, logos, scopes, etc.). Gives Data Providers visibility into which apps consumers are connecting to without implementing Dynamic Client Registration.`}</p>
      <h2 {...{
        "id": "why-oauth"
      }}>{`Why OAuth?`}</h2>
      <h3 {...{
        "id": "modern-api-based-authentication"
      }}>{`Modern API-based authentication`}</h3>
      <p>{`OAuth delivers secure, consumer-controlled access to financial data. Consumers authenticate directly with their bank, and the Data Provider issues a secure, scoped token to Plaid using industry-standard protocols.`}</p>
      <h3 {...{
        "id": "what-you-get"
      }}>{`What you get`}</h3>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Security:`}</strong>{` Credentials stay with you. Tokens are scoped, revocable, and expire.`}</li>
        <li parentName="ul"><strong parentName="li">{`Better UX:`}</strong>{` Enable returning user flows and App2App authentication.`}</li>
        <li parentName="ul"><strong parentName="li">{`Compliance ready:`}</strong>{` Meet regulatory requirements and align with FDX standards.`}</li>
        <li parentName="ul"><strong parentName="li">{`Industry standard:`}</strong>{` One integration works across multiple DAPs.`}</li>
      </ul>
      <h3 {...{
        "id": "how-oauth-works-with-plaid-core-exchange"
      }}>{`How OAuth works with Plaid Core Exchange`}</h3>
      <p>{`Plaid acts as a Data Access Platform (DAP), the registered OAuth client that handles the authorization flow and manages consent across multiple apps (Authorized Third Parties, or ATPs).`}</p>
      <Image src='/assets/img/core-exchange/sequence-diagram.png' alt='OAuth sequence diagram showing the flow between Consumer, Plaid, and Data Provider' mdxType="Image" />
      <h2 {...{
        "id": "fdx-version-requirements"
      }}>{`FDX version requirements`}</h2>
      <p>{`Core Exchange is built on Financial Data Exchange (FDX) standards and supports all FDX versions 4.6 and above. If you've already built an FDX integration, it will work with Core Exchange out of the box.`}</p>
      <p><strong parentName="p">{`What Plaid supports:`}</strong></p>
      <ul>
        <li parentName="ul"><strong parentName="li">{`FDX 4.6+:`}</strong>{` All versions are supported by Core Exchange`}</li>
        <li parentName="ul"><strong parentName="li">{`Latest FDX version:`}</strong>{` Recommended for new integrations to access the newest features.`}</li>
      </ul>
      <p>{`For new integrations, build to the latest FDX version to access new features and align with evolving industry standards.`}</p>
      <h2 {...{
        "id": "questions"
      }}>{`Questions?`}</h2>
      <p>{`Reach out to your Solutions Engineer for more resources.`}</p>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "what-youll-learn",
        level: 2,
        title: "What you'll learn",
      },
{
        id: "overview",
        level: 2,
        title: "Overview",
      },
{
        id: "try-the-interactive-demo",
        level: 2,
        title: "Try the interactive demo",
      },
{
        id: "key-terms",
        level: 2,
        title: "Key terms",
      },
{
        id: "parties-the-who",
        level: 3,
        title: "Parties (the who)",
      },
{
        id: "protocols-and-standards-the-framework",
        level: 3,
        title: "Protocols and standards (the framework)",
      },
{
        id: "token-models-and-types-the-what",
        level: 3,
        title: "Token models and types (the what)",
      },
{
        id: "key-concepts-the-how",
        level: 3,
        title: "Key concepts (the how)",
      },
{
        id: "management-tools-the-where",
        level: 3,
        title: "Management tools (the where)",
      },
{
        id: "why-oauth",
        level: 2,
        title: "Why OAuth?",
      },
{
        id: "modern-api-based-authentication",
        level: 3,
        title: "Modern API-based authentication",
      },
{
        id: "what-you-get",
        level: 3,
        title: "What you get",
      },
{
        id: "how-oauth-works-with-plaid-core-exchange",
        level: 3,
        title: "How OAuth works with Plaid Core Exchange",
      },
{
        id: "fdx-version-requirements",
        level: 2,
        title: "FDX version requirements",
      },
{
        id: "questions",
        level: 2,
        title: "Questions?",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
